// «Расписание»: неделя или месяц, под ними шкала выбранного дня (часы по поясам, обед, личные дела, сессии, окна,
// «сейчас»); масштаб шкалы — кнопками или щипком двумя пальцами.
import { RU_DAYS, RU_DAYS_FULL, RU_MONTHS_GEN, STATUS, addDays, addMonths, dayCapacity, daysInMonth, dowOf, fitsLabel,
  isCommercial, isHeld, minToHm, monthOf, occupies, pad2, plural, ruDate, ruMonth, weekStartOf,
  type FreeWindow } from '@praktika/core';
import { busyAllDay, busyDayNote, busyTimed, ensureBusy } from '../lib/busy.ts';
import { colorCss, colorOf, dotAttr, resVars } from '../lib/colors.ts';
import { $, $$, cap, esc, store } from '../lib/dom.ts';
import { clientById, clientTimeLabel, daySessions, defaultType, groupLabel, groupOf, meetShort, statusLabel, windowsOf,
  workOf, type DayItem } from '../lib/data.ts';
import { SCREENS, fab, go, render, setTab } from '../lib/nav.ts';
import { S, UI } from '../lib/state.ts';
import { atOff, nowInfo, tzChipsHtml, zoneColW, zoneGroups, zoneLabel } from '../lib/zones.ts';
import type { WorkDay } from '@praktika/core';
import { bindCommon } from './common.ts';

type Now = { date: string; min: number };
type TlArgs = [day: string, work: WorkDay | null, ds: DayItem[], win: FreeWindow[], n: Now];
let tlArgs: TlArgs | null = null;

const ZOOMS: Array<[string, string]> = [['fit', 'Весь день'], ['normal', 'Обычно'], ['large', 'Крупно']];

function ppmFor(range: number): number {
  const z = UI.zoom || 'normal';
  if (z === 'large') return 1.9;
  if (z === 'custom') return Math.max(0.45, Math.min(3, Number(UI.ppm) || 1.15));
  if (z === 'fit') {
    const avail = (window.innerHeight || 800) - (UI.tlTop || 330) - 64 - 14;
    return Math.max(0.45, Math.min(1.9, avail / Math.max(range, 120)));
  }
  return 1.15;
}

function tlBarHtml(): string {
  const z = UI.zoom || 'normal';
  return '<div class="tlbar">' + tzChipsHtml() + '</div><div class="tlbar zrow"><div class="zoom">' +
    ZOOMS.map((o) => '<button' + (o[0] === z ? ' class="on"' : '') + ' data-zoom="' + o[0] + '">' + o[1] + '</button>')
      .join('') + '</div></div>';
}

// «Весь день»: после первой отрисовки узнаём, где начинается шкала, и перерисовываем под высоту экрана
function fitTimeline(app: HTMLElement): void {
  const tl = $('.tl', app);
  if (!tl) return;
  const top = Math.round(tl.getBoundingClientRect().top + window.scrollY);
  if ((UI.zoom || 'normal') !== 'fit' || UI._refit) { UI.tlTop = top; return; }
  if (Math.abs((UI.tlTop || 0) - top) > 4) {
    UI.tlTop = top;
    UI._refit = true;
    try { render(); } finally { UI._refit = false; }
  }
}

function remountTimeline(): void {
  const tl = $('#app .tl');
  if (!tl || !tlArgs) return;
  const host = document.createElement('div');
  host.innerHTML = timelineHtml(...tlArgs);
  const nt = host.querySelector<HTMLElement>('.tl');
  if (nt) { tl.parentNode!.replaceChild(nt, tl); bindCommon(nt); }
}

// щипок двумя пальцами по шкале — плавный масштаб
// Шкале задан touch-action: pan-y — один палец листает страницу, два пальца достаются нам. Точка между пальцами остаётся на месте.
{
  let d0 = 0, p0 = 0, m0 = 0, midY = 0, raf = 0;
  const dist = (e: TouchEvent) => {
    const a = e.touches[0]!, b = e.touches[1]!;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };
  const mid = (e: TouchEvent) => (e.touches[0]!.clientY + e.touches[1]!.clientY) / 2;
  document.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 2) return;
    const tl = $('#app .tl');
    if (!tl || !(e.target as HTMLElement).closest?.('.tl')) return;
    d0 = dist(e);
    p0 = UI.ppmNow || 1.15;
    midY = mid(e);
    m0 = (midY - tl.getBoundingClientRect().top) / p0;
  }, { passive: true });
  document.addEventListener('touchmove', (e) => {
    if (e.touches.length !== 2 || !d0) return;
    UI.zoom = 'custom';
    UI.ppm = Math.max(0.45, Math.min(3, p0 * dist(e) / d0));
    midY = mid(e);
    if (!raf) raf = requestAnimationFrame(() => {
      raf = 0;
      remountTimeline();
      const tl = $('#app .tl');
      if (tl) window.scrollBy(0, tl.getBoundingClientRect().top + m0 * UI.ppm! - midY);
    });
  }, { passive: true });
  const finish = () => {
    if (!d0) return;
    d0 = 0;
    store('praktika_zoom', 'custom:' + Number(UI.ppm).toFixed(2));
    const y = window.scrollY;
    render();
    window.scrollTo(0, y);
  };
  document.addEventListener('touchend', finish, { passive: true });
  document.addEventListener('touchcancel', finish, { passive: true });
}

function schedSeg(): string {
  const m = UI.schedMode === 'month';
  return '<div class="seg" style="margin-top:12px"><button' + (m ? '' : ' class="on"') + ' data-smode="week">Неделя</button>' +
    '<button' + (m ? ' class="on"' : '') + ' data-smode="month">Месяц</button></div>';
}

function daySum(day: string, n: Now, withMonth: boolean): string {
  const occ = daySessions(day).filter((x) => isHeld(x.status)), work = workOf(day), std = defaultType();
  return '<div class="p-sum">' + esc(cap(RU_DAYS_FULL[dowOf(day)]!) + ' ' + (withMonth ? ruDate(day) : Number(day.slice(8)))) +
    ' · <b>' + occ.length + ' ' + plural(occ.length, 'сессия', 'сессии', 'сессий') + '</b>' +
    (day >= n.date && work ? ' · можно добавить <b>' + dayCapacity(windowsOf(day)) + ' по ' + std.dur + '</b>' : '') +
    (!work ? ' · выходной' : '') + busyDayNote(day) + '</div>';
}

SCREENS.sched = (app) => {
  const n = nowInfo();
  if (!UI.schedDay) UI.schedDay = n.date;
  ensureBusy(UI.schedDay);
  if (UI.schedMode === 'month') { schedMonth(app, n); return; }
  const ws = weekStartOf(UI.schedDay), days: string[] = [];
  for (let i = 0; i < 7; i++) days.push(addDays(ws, i));
  const std = defaultType();
  let weekSess = 0, weekCap = 0;
  for (const d of days) {
    weekSess += daySessions(d).filter((x) => isHeld(x.status)).length;
    if (d >= n.date) weekCap += dayCapacity(windowsOf(d));
  }
  const d0 = days[0]!, d6 = days[6]!;
  const range = Number(d0.slice(8)) + (monthOf(d0) === monthOf(d6) ? '' : ' ' + RU_MONTHS_GEN[Number(d0.slice(5, 7)) - 1]) +
    '–' + ruDate(d6);
  let html = schedSeg() + '<div class="p-head">' +
    '<button data-week="-1" style="font-size:24px;color:var(--muted);padding:0 8px 0 0">‹</button>' +
    '<span class="d" style="flex:1">' + esc(range) +
    '</span><button data-week="1" style="font-size:24px;color:var(--muted);padding:0 0 0 8px">›</button><span class="m">' +
    weekSess + ' ' + plural(weekSess, 'сессия', 'сессии', 'сессий') + ' · ещё ' + weekCap + ' по ' + std.dur +
    '</span></div><div class="days">';
  for (const d of days) {
    const ds = daySessions(d).filter((x) => occupies(x.status)), w = workOf(d);
    html += '<button class="day' + (d === UI.schedDay ? ' sel' : '') + (!w ? ' off' : '') + (d === n.date ? ' today' : '') +
      (busyAllDay(d).length ? ' allbz' : '') + '" data-d="' + d + '"><div class="w">' + RU_DAYS[dowOf(d)]!.toLowerCase() +
      '</div><div class="n">' + Number(d.slice(8)) + '</div><div class="dots">' +
      ds.slice(0, 5).map((x) => '<i' + dotAttr(x.sess) + '></i>').join('') +
      (busyTimed(d).length ? '<i class="b"></i>' : '') + '</div></button>';
  }
  html += '</div>';
  const day = UI.schedDay;
  html += daySum(day, n, false) + timelineHtml(day, workOf(day), daySessions(day), windowsOf(day), n);
  app.innerHTML = html;
  $$('[data-week]', app).forEach((b) => {
    b.onclick = () => { UI.schedDay = addDays(UI.schedDay!, 7 * Number(b.getAttribute('data-week'))); render(); };
  });
  $$('[data-d]', app).forEach((b) => { b.onclick = () => { UI.schedDay = b.getAttribute('data-d'); render(); }; });
  bindCommon(app);
  fab('Записать', () => go('book', { date: UI.schedDay }));
  fitTimeline(app);
};

// Шкала дня: часы моего пояса (и дополнительных поясов колонками слева), обед, личные дела, сессии в цвете клиента,
// окна, «сейчас».
function timelineHtml(day: string, work: WorkDay | null, ds: DayItem[], win: FreeWindow[], n: Now): string {
  if (!work) return '<div class="empty">Выходной. Записать можно всё равно: нажмите «Записать» и выберите этот день — ' +
    'но окон в выходной нет, скрипт предложит поправить график.</div>';
  let start = work.start, end = work.end;
  for (const x of ds) {
    if (x.start < start) start = Math.floor(x.start / 60) * 60;
    if (x.start + x.dur > end) end = Math.ceil((x.start + x.dur) / 60) * 60;
  }
  // личные дела в пределах часа от рабочего дня тоже растягивают шкалу; далёкие (утренняя пробежка) не показываем
  const timed = busyTimed(day).filter((b) => b.end > work.start - 60 && b.start < work.end + 60);
  for (const b of timed) {
    if (b.start < start) start = Math.max(0, Math.floor(b.start / 60) * 60);
    if (b.end > end) end = Math.min(1440, Math.ceil(b.end / 60) * 60);
  }
  // колонки времени: города с одинаковым временем — одна колонка; главная (подписи окон, линии часов) — крайняя справа,
  // у блоков. Над колонками — названия городов полностью; шаг колонки — по самому длинному названию (не меньше 50 px).
  const ppm = ppmFor(end - start), groups = zoneGroups(day), ng = groups.length, STEP = zoneColW(groups, 50),
    main = groups[ng - 1]!.zones[0]!, mainOff = groups[ng - 1]!.off, left = ng * STEP + 4, sm = ppm < 0.8;
  const zt = (t: number) => atOff(t, mainOff);
  UI.ppmNow = ppm;
  tlArgs = [day, work, ds, win, n];
  const out = [tlBarHtml()];
  if (ng > 1 || groups[0]!.zones.length > 1 || main !== S.tz) out.push('<div class="tlhead">' + groups.map((g) =>
    '<span' + (g.main ? ' class="m"' : '') + ' style="width:' + STEP + 'px">' + zoneLabel(g, '<br>') + '</span>').join('') +
    '</div>');
  out.push('<div class="tl' + (sm ? ' sm' : '') + '" style="height:' + Math.round((end - start) * ppm + 10) +
    'px;margin-left:' + left + 'px">');
  groups.forEach((g, i) => {
    const off = g.off, x = 42 + (ng - 1 - i) * STEP;
    for (let te = Math.ceil((start + off) / 60) * 60; te <= end + off; te += 60) {
      const yy = (te - off - start) * ppm;
      if (yy < -2 || yy > (end - start) * ppm + 2) continue;
      out.push('<div class="hr' + (g.main ? '' : ' ex') + '" style="top:' + yy + 'px;left:-' + x + 'px">' +
        minToHm(((te % 1440) + 1440) % 1440) + '</div>' + (g.main ? '<div class="ln" style="top:' + yy + 'px"></div>' : ''));
    }
  });
  if (work.lunchStart !== null && work.lunchStart !== undefined && work.lunchEnd !== null && work.lunchEnd !== undefined) {
    out.push('<div class="bk lunch" style="top:' + ((work.lunchStart - start) * ppm + 1) + 'px;height:' +
      ((work.lunchEnd - work.lunchStart) * ppm - 2) + 'px">обед</div>');
  }
  for (const b of timed) {
    const s0 = Math.max(b.start, start), e0 = Math.min(b.end, end);
    if (e0 <= s0) continue;
    out.push('<div class="bk bz" style="top:' + ((s0 - start) * ppm + 1) + 'px;height:' + Math.max((e0 - s0) * ppm - 2, 12) +
      'px"><span class="tx">' + esc(b.title) + ' · ' + zt(b.start) + '–' + zt(b.end) + (b.cal ? ' · ' + esc(b.cal) : '') +
      '</span></div>');
  }
  for (const x of ds) {
    const s = x.sess, top = (x.start - start) * ppm, h = x.dur * ppm, proj = isCommercial(groupOf(s));
    const c = clientById(s.clientId), ct = clientTimeLabel(s, c), col = c && occupies(x.status) ? colorOf(c) : '';
    const res = x.status === STATUS.RESERVE;
    out.push('<button class="bk sess' + (proj ? ' p' : '') + (res ? ' res' : '') + (!occupies(x.status) ? ' off' : '') +
      '" style="top:' + (top + 1) + 'px;height:' + Math.max(h - 2, 14) + 'px' + (res ? ';' + resVars(c, s) : col ?
      ';background:' + colorCss(col).bg + ';color:' + colorCss(col).fg : '') + '" data-sess-open="' + esc(x.id) +
      '"><span class="tx"><b>' + esc(x.name) + '</b> · ' + (res ? 'резерв · ' : '') + esc(groupLabel(s)) + ' · ' + x.dur +
      ' мин · ' + esc(meetShort(s)) + (ct ? ' · ' + esc(ct) : '') +
      (!occupies(x.status) ? ' · ' + esc(statusLabel(s.status).toLowerCase()) : x.status === STATUS.DONE ? ' ✓' : '') +
      '</span></button>');
    if (isHeld(x.status) && x.brk) out.push('<div class="bk brk" style="top:' + ((x.start + x.dur - start) * ppm + 1) +
      'px;height:' + Math.max(Math.min(x.brk, end - x.start - x.dur) * ppm - 2, 8) + 'px">перерыв ' + x.brk + '</div>');
  }
  for (const w of win) out.push('<button class="bk free" style="top:' + ((w.fs - start) * ppm + 1) + 'px;height:' +
    Math.max((w.fe - w.fs) * ppm - 2, 20) + 'px" data-book="' + w.fs + '" data-book-date="' + day + '" data-book-tz="' +
    esc(main) + '"><span class="tx">свободно ' + zt(w.fs) + '–' + zt(w.fe) + ' · ' + esc(fitsLabel(w.fits)) +
    '</span><span class="plus">+</span></button>');
  if (day === n.date && n.min >= start && n.min <= end) out.push('<div class="now" style="top:' + ((n.min - start) * ppm) +
    'px"></div>');
  out.push('</div>');
  return out.join('');
}

function schedMonth(app: HTMLElement, n: Now): void {
  const ym = monthOf(UI.schedDay!), first = ym + '-01', last = ym + '-' + pad2(daysInMonth(ym)), start = weekStartOf(first);
  const bad: Record<string, boolean> = {};
  for (const k of S.conflicts || []) bad[k.date] = true;
  const cells: string[] = [];
  let d = start, total = 0;
  while (d <= last || dowOf(d) !== 0) {
    const ds = daySessions(d).filter((x) => occupies(x.status)), inM = monthOf(d) === ym, w = workOf(d),
      nh = ds.filter((x) => isHeld(x.status)).length;
    if (inM) total += nh;
    cells.push('<button class="mcell' + (inM ? '' : ' out') + (!w ? ' off' : '') + (d === n.date ? ' today' : '') +
      (d === UI.schedDay ? ' sel' : '') + (bad[d] ? ' bad' : '') + (busyAllDay(d).length ? ' allbz' : '') +
      '" data-md="' + d + '"><span class="n">' + Number(d.slice(8)) + '</span><span class="c">' + (nh ? nh : '') +
      '</span><span class="dots">' + ds.slice(0, 4).map((x) => '<i' + dotAttr(x.sess) + '></i>').join('') +
      (busyTimed(d).length ? '<i class="b"></i>' : '') + '</span></button>');
    d = addDays(d, 1);
    if (cells.length > 42) break;
  }
  let html = schedSeg() + '<div class="p-head">' +
    '<button data-month="-1" style="font-size:24px;color:var(--muted);padding:0 8px 0 0">‹</button>' +
    '<span class="d" style="flex:1">' + esc(cap(ruMonth(ym))) +
    '</span><button data-month="1" style="font-size:24px;color:var(--muted);padding:0 0 0 8px">›</button><span class="m">' +
    total + ' ' + plural(total, 'сессия', 'сессии', 'сессий') + '</span></div>' +
    '<div class="mhead">' + [0, 1, 2, 3, 4, 5, 6].map((i) => '<span>' + RU_DAYS[i]!.toLowerCase() + '</span>').join('') +
    '</div><div class="mgrid">' + cells.join('') + '</div>';
  const day = UI.schedDay!;
  html += daySum(day, n, true) + timelineHtml(day, workOf(day), daySessions(day), windowsOf(day), n);
  app.innerHTML = html;
  $$('[data-month]', app).forEach((b) => {
    b.onclick = () => {
      const t = addMonths(ym, Number(b.getAttribute('data-month')));
      UI.schedDay = monthOf(n.date) === t ? n.date : t + '-01';
      render();
    };
  });
  $$('[data-md]', app).forEach((b) => {
    b.onclick = () => { UI.schedDay = b.getAttribute('data-md'); const y = window.scrollY; render(); window.scrollTo(0, y); };
  });
  bindCommon(app);
  fab('Записать', () => go('book', { date: UI.schedDay }));
  fitTimeline(app);
}

document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  const sm = t.closest?.('[data-smode]');
  if (sm) {
    UI.schedMode = sm.getAttribute('data-smode') === 'month' ? 'month' : 'week';
    store('praktika_sched', UI.schedMode);
    render();
    return;
  }
  const zb = t.closest?.('[data-zoom]');
  if (zb) { UI.zoom = zb.getAttribute('data-zoom')!; store('praktika_zoom', UI.zoom); render(); return; }
  // нажатие на строку ЧП открывает расписание этого дня
  const cf = t.closest?.('[data-conf]');
  if (cf) { UI.schedDay = cf.getAttribute('data-conf'); setTab('sched'); }
});

// что было выбрано в прошлый раз: неделя или месяц, масштаб шкалы, скрытые пояса
UI.schedMode = store('praktika_sched') === 'month' ? 'month' : 'week';
{
  const zs = store('praktika_zoom') || 'normal';
  if (zs.startsWith('custom:')) { UI.zoom = 'custom'; UI.ppm = Number(zs.slice(7)) || 1.15; } else UI.zoom = zs;
  try { UI.tzOff = JSON.parse(store('praktika_tzoff') || '{}') || {}; } catch { UI.tzOff = {}; }
}
